import {
  type ClientRect,
  type CollisionDescriptor,
  type CollisionDetection,
  type DragOverEvent,
  type Modifier,
} from '@dnd-kit/core';
import { type Row, type RowData } from '@tanstack/react-table';

import Animations from '@dynatrace/strato-design-tokens/animations';

import { getDataTableBorderWidth } from '../../../utils/data-table-border-width.js';
import {
  DATA_TABLE_DEFAULT_MIN_ROW_HEIGHT,
  DATA_TABLE_PINNING_SHADOW_CELL_ROW_ID_SUFFIX,
  DATA_TABLE_ROW_BORDER,
  DATA_TABLE_ROW_EXPANDED_ID_SUFFIX,
} from '../../constants.js';
import type { DragAndDropTransform } from '../types/dnd.types.js';
import type { TDataTableFeatures } from '../types/features.types.js';
import { disabledNonTargetRow } from './RowOrder.sty.js';

// #region RowTransforms
// all rows that were transformed in the last onDragOver call
let lastAffectedRows = new Set<string>();

/**
 * Transforms row upwards or downwards to make space for the row which is currently being dragged ("active" row).
 * @param table - the table where the row ordering is happening.
 * @param event - contains "active" (row being dragged) and "over" ids (row where the active row is currently dragged over).
 * @param rowOrder - original row order before dragging.
 * @param rowSeparationNone - whether the rows are visually separated by a border.
 * @param activeRow - the currently dragged row.
 * @internal
 */
export function transformRowOnDragOver<TData extends RowData>(
  table: HTMLElement,
  event: DragOverEvent,
  rowOrder: string[] | undefined,
  rowSeparationNone: boolean,
  activeRow?: Row<TDataTableFeatures, TData>,
): void {
  let rowHeight = event.active?.rect.current.translated?.height;

  if (
    activeRow === undefined ||
    rowOrder === undefined ||
    event.collisions === null ||
    event.active === null ||
    event.over === null ||
    rowHeight === undefined
  ) {
    return;
  }

  // visually disable rows that do not share the same parent as the dragged row
  styleNonTargetRows(table, activeRow.parentId);

  // keep track of all rows that are transformed during the current onDragOver call
  const newAffectedRows = new Set<string>();
  const { fromIndex, toIndex } = getDragIndexes(
    rowOrder,
    event.active.id as string, // for us the IDs are always strings
    event.over.id as string, // for us the IDs are always strings
  );

  const activeRowIsLastRow = fromIndex === rowOrder.length - 1;

  // if rowSeparation is active, consider 1px border for all rows
  // except if dragging the last row or if row details are open
  if (
    !rowSeparationNone &&
    !activeRowIsLastRow &&
    !activeRow.getIsDetailsOpen()
  ) {
    rowHeight += DATA_TABLE_ROW_BORDER;
  }

  if (fromIndex < toIndex) {
    transformRows(
      table,
      rowOrder.slice(fromIndex + 1, toIndex + 1),
      -rowHeight,
      newAffectedRows,
    );
  } else if (fromIndex > toIndex) {
    transformRows(
      table,
      rowOrder.slice(toIndex, fromIndex),
      rowHeight,
      newAffectedRows,
    );
  }

  // Reset rows that were translated in the last drag operation but are not in the current one
  transformRows(table, lastAffectedRows, 0);
  lastAffectedRows = newAffectedRows;
}

/**
 * Moves rows on the y-axis by the given number of pixels.
 * @param table - the table grid element.
 * @param rowIds - the ids of rows to transform.
 * @param amountToTransform - number of pixels to translate (positive for down or negative for up)
 * @param newAffectedRows - set to save all rows that are transformed during the current onDragOver call
 */
function transformRows(
  table: HTMLElement,
  rowIds: string[] | Set<string>,
  amountToTransform: number,
  newAffectedRows?: Set<string>,
): void {
  for (const rowId of rowIds) {
    const row = getRowWithId(table, rowId);
    if (row.length <= 0) {
      continue;
    }
    translateRowY(row, amountToTransform);
    if (newAffectedRows) {
      lastAffectedRows.delete(rowId);
      newAffectedRows.add(rowId);
    }
  }
}

/**
 * Applies styles to visually indicate droppable targets for the currently active row.
 * Rows that do not share the same parent as the dragged row will appear visually disabled.
 *
 * @param table - The table element containing the rows to style.
 * @param activeParentId - parent id of the currently dragged row.
 */
function styleNonTargetRows(table: HTMLElement, activeParentId?: string): void {
  // if the dragged row is a top-level row, we don't want to add any styles
  if (activeParentId === undefined) {
    return;
  }
  const rows = getAllNonTargetRowsInTableBody(table, activeParentId);
  for (const row of rows) {
    row.classList.add(disabledNonTargetRow);
  }
}

/**
 * Moves row on the y-axis by the given number of pixels. Applies a transition
 * also to make it looks smoother, using strato tokens for duration and easing.
 * @param row - the row to translate
 * @param value - number of pixels (positive for down or negative for up)
 */
function translateRowY(row: HTMLElement[], value: number): void {
  const transform = `translateY(${value}px)`;
  const transition = `transform ${Animations.SlideIn.Duration.Base} ${Animations.Move.Easing}`;
  for (const rowElement of row) {
    rowElement.style.transform = transform;
    rowElement.style.transition = transition;
  }
}

/**
 * Resets the transform style on the given row.
 * @param rows - the row we want to reset.
 * @internal
 */
export function resetAllStylesForRows(rows: HTMLElement[]): void {
  if (rows.length === 0) {
    return;
  }
  for (const row of rows) {
    row.style.removeProperty('transform');
    row.style.removeProperty('transition');
    row.classList.remove(disabledNonTargetRow);
  }
}

/**
 * Gets the row elements with a given row id (including row details row).
 * @param table - the current table.
 * @param id - id of the row (matches against `data-rowid`).
 * @returns matching rows.
 * @internal
 */
export function getRowWithId(
  table: HTMLElement,
  id: string | number | undefined,
): HTMLElement[] {
  if (id === undefined) {
    return [];
  }
  const rowElements = table.querySelectorAll<HTMLElement>(
    `[data-rowid="${CSS.escape(String(id))}"], [data-rowid="${CSS.escape(
      String(id),
    )}${DATA_TABLE_ROW_EXPANDED_ID_SUFFIX}"], [data-rowid="${CSS.escape(
      String(id),
    )}${DATA_TABLE_PINNING_SHADOW_CELL_ROW_ID_SUFFIX}"]`,
  );

  return Array.from(rowElements);
}

/**
 * Gets all rows in the given table that are not within the same sub-row parent.
 *
 * @param table - the table we are concerned with.
 * @param parentIdToExclude - parent id to be excluded
 * @returns all rows in the table (div elements with role="row").
 * @internal
 */
export function getAllNonTargetRowsInTableBody(
  table: HTMLElement,
  parentIdToExclude?: string,
): HTMLElement[] {
  const nonTargetRows = table.querySelectorAll<HTMLElement>(
    `[data-rowgroup="body"] [role="row"]:not([data-rowid^="${CSS.escape(
      String(parentIdToExclude),
    )}"]`,
  );

  return Array.from(nonTargetRows);
}

/**
 * Gets all rows in the given table (all rows that have role="row" and a data-rowid attribute).
 * @param table - the table we are concerned with.
 * @returns all rows in the table.
 * @internal
 */
export function getAllRowsInTableBody(table: HTMLElement): HTMLElement[] {
  const allRowsInTableBody = table.querySelectorAll<HTMLElement>(
    '[role="row"][data-rowid], [role="presentation"][data-rowid]',
  );

  return Array.from(allRowsInTableBody);
}

/**
 * @param table - the table we are concerned with.
 * @param rowNumber - zero-based row number
 * @returns the drag handle of the given row for drag and drop ordering
 * @internal
 */
export function getDragHandleForRow(
  table: HTMLElement,
  rowNumber: number,
): HTMLElement | null {
  return table.querySelector<HTMLElement>(
    `[role="row"][data-row="${rowNumber}"] [role="cell"][data-column="0"] button`,
  );
}
// #endregion RowTransforms

/**
 * Gets the `fromIndex` and `toIndex` from the over and active id.
 * @param rowOrder - contains the ids of the rows in order.
 * @param activeId - is the id of the currently actively dragged row
 * @param overId - is the target id of where the dragging row is currently dragged to
 * @returns indexes from where a row is dragged and where it is dragged to
 * @internal
 */
export function getDragIndexes(
  rowOrder: string[],
  activeId: string,
  overId: string,
): { fromIndex: number; toIndex: number } {
  const fromIndex = rowOrder.findIndex((id) => id === activeId);
  const toIndex = rowOrder.findIndex((id) => id === overId);
  return { fromIndex, toIndex };
}

// #region CollisionDetection
/**
 * Check if a given point is contained within a bounding rectangle
 */
function isPointWithinRectY(pointY: number, rect: ClientRect): boolean {
  return rect.top <= pointY && pointY <= rect.bottom;
}

export const DATA_TABLE_ROW_DND_COLLISION_OFFSET =
  DATA_TABLE_DEFAULT_MIN_ROW_HEIGHT / 2 - 1;

/**
 * Custom collision detection algorithm for row order drag and drop.
 * Determines the closest droppable target within the Y-axis based on the dragging direction.
 *
 * @param param0 - Object containing the droppable containers, the droppable rects, the collision rect and
 *                 the active element, which are needed for the collision detection.
 * @returns An array of collision descriptors, sorted by the distance to the dragged row, or an empty array if none is found.
 * @internal
 */
export const closestTargetWithinY: CollisionDetection = ({
  droppableContainers,
  droppableRects,
  collisionRect,
  active,
}) => {
  const initialRect = active.rect.current.initial;
  if (initialRect === null) {
    return [];
  }

  const collisions: CollisionDescriptor[] = [];

  // To make this work for varying row heights, depending on the dragging direction
  // we take either the top or the bottom of the dragged row to check for collisions
  // (minus a small offset, so the rows have to overlap a bit, before they would be swapped)
  const isDraggingUp = collisionRect.top < initialRect.top;
  // min row height is 24px, to ensure the offset is in the top/bottom half: 24 / 2 - 1 = 11
  const collisionRectY = isDraggingUp
    ? collisionRect.top + DATA_TABLE_ROW_DND_COLLISION_OFFSET
    : collisionRect.bottom - DATA_TABLE_ROW_DND_COLLISION_OFFSET;

  for (const droppableContainer of droppableContainers) {
    const { id, node } = droppableContainer;
    const rect = droppableRects.get(id);
    if (rect === undefined) {
      continue;
    }

    // the droppable containers do not consider the row details height, so we need to get that
    // this can be removed once we have subgrids and can put the ref on the row itself (APPDEV-12251)
    const heightAdaptedRect = { ...rect };
    const rowDetailsElement = node.current?.parentElement?.querySelector(
      `[data-rowid="${CSS.escape(
        String(id),
      )}${DATA_TABLE_ROW_EXPANDED_ID_SUFFIX}"]`,
    );

    if (rowDetailsElement) {
      const detailsHeight = rowDetailsElement.getBoundingClientRect().height;
      heightAdaptedRect.height += detailsHeight;
      heightAdaptedRect.bottom += detailsHeight;
    }

    if (isPointWithinRectY(collisionRectY, heightAdaptedRect)) {
      // return the container that the active rect is dragged over
      return [
        {
          id,
          data: { droppableContainer },
        },
      ];
    }

    // in case the dragged row's collisionRectY is not directly within any of the droppable containers
    // (e.g. when dragging over the scrollbar) we need to find the closest one. Therefore, we calculate
    // the distance between the droppable container's top/bottom and the collisionRectY coordinate.
    const droppableContainerY = isDraggingUp ? rect.top : rect.bottom;
    const distance = Math.abs(collisionRectY - droppableContainerY);
    collisions.push({ id, data: { droppableContainer, value: distance } });
  }

  return collisions.toSorted((a, b) => a.data.value - b.data.value);
};

/**
 * Identifies the last row in the given array where drag-and-drop is disabled, and where all preceding rows are also disabled.
 *
 * @param rows - the table rows
 * @returns the index of the last found row with disabled drag and drop
 */
function findLastDisabledRow<TData extends RowData>(
  rows: { rowIndex: number; row: Row<TDataTableFeatures, TData> }[],
) {
  let lastDisabledRowIndex: number | undefined = undefined;
  for (const { rowIndex, row } of rows) {
    if (row.getCanDrag()) {
      break;
    }
    lastDisabledRowIndex = rowIndex;
  }
  return lastDisabledRowIndex;
}

/**
 * Custom collision detection function that filters droppable containers based the sub-row depth of the dragged row,
 * and then applies the custom closestCenterWithinY algorithm on the filtered targets.
 * Only rows on the same sub-row depth level can be ordered.
 *
 * We need to pass the dragged row stored in the table state, as during dragging in a scrollable table it can happen
 * that the active row is virtualized and would therefore be undefined.
 *
 * @param draggingRowInfo - active/dragged row info saved in the table state
 * @returns
 */
export function createRowOrderCollisionDetection<TData extends RowData>(
  draggingRowInfo: {
    id: string;
    index: number;
    row: Row<TDataTableFeatures, TData>;
  } | null,
): CollisionDetection {
  return ({ active, droppableContainers, ...rest }) => {
    if (draggingRowInfo === null) {
      return [];
    }
    const activeRow = draggingRowInfo.row;
    const activeRowIndex = draggingRowInfo.index;
    const activeRowSubRowDepth = activeRow.getSubRowDepth();

    const lastExpandedSubRowsOnLevel = new Set<string>();

    const validTargets = droppableContainers.filter((target) => {
      const targetRow = target.data.current?.row as Row<
        TDataTableFeatures,
        RowData
      >;
      const targetRowIndex = target.data.current?.rowIndex;
      const targetRowSubRowDepth = targetRow?.getSubRowDepth();
      const targetRowDisabled = !targetRow?.getCanDrag();
      const targetRowLockedDisabled =
        targetRow?.getDisabledRowDragAndDropLockPosition?.();

      const isSameDepthAndParent =
        targetRowSubRowDepth === activeRowSubRowDepth &&
        activeRow.parentId === targetRow.parentId;

      // currently active row is a valid target
      if (activeRowIndex === targetRowIndex) {
        return true;
      }

      if (targetRowDisabled && targetRowLockedDisabled !== false) {
        const rowsByRowIndex = droppableContainers
          .filter(
            (container) => container.data.current?.row.parentId === undefined,
          )
          .map((container) => ({
            rowIndex: container.data.current?.rowIndex,
            row: container.data.current?.row,
          }))
          .toSorted((data1, data2) => data1?.rowIndex - data2?.rowIndex);

        const lastDisabledRowIndexFromStart =
          targetRowLockedDisabled !== 'end'
            ? findLastDisabledRow(rowsByRowIndex)
            : undefined;
        const firstDisabledRowIndexFromEnd =
          targetRowLockedDisabled !== 'start'
            ? findLastDisabledRow(rowsByRowIndex.toReversed())
            : undefined;

        // if the disabled row is already (grouped) at the start or end,
        // the row is not a valid target
        if (
          targetRow.parentId === undefined &&
          ((lastDisabledRowIndexFromStart !== undefined &&
            targetRowIndex <= lastDisabledRowIndexFromStart) ||
            (firstDisabledRowIndexFromEnd !== undefined &&
              targetRowIndex >= firstDisabledRowIndexFromEnd))
        ) {
          return false;
        }
      }

      if (isSameDepthAndParent) {
        const hasOpenSubRows =
          targetRow.getCanExpand() && targetRow.getIsExpanded();

        // when active row is moved down, and the target row has expanded sub-rows the row
        // should only be moved when it's moved past the sub-rows
        // therefore, instead of the parent row, the last expanded sub-row is the valid target
        if (activeRowIndex < targetRowIndex && hasOpenSubRows) {
          // get id of last open subrow and save it to be able to make it the actual target
          const lastExpandedRow = targetRow.getLastExpandedSubRow();
          if (lastExpandedRow) {
            lastExpandedSubRowsOnLevel.add(lastExpandedRow.id);
          }
          return false;
        }

        return true;
      }

      if (targetRowSubRowDepth > activeRowSubRowDepth) {
        // if the target is the last expanded sub-row of a target row
        return lastExpandedSubRowsOnLevel.has(targetRow.id);
      }

      return false;
    });

    // Apply closestTargetWithinY on filtered targets
    return closestTargetWithinY({
      active,
      droppableContainers: validTargets,
      ...rest,
    });
  };
}
// #endregion CollisionDetection

// #region Modifiers
/**
 * Restricts the vertical movement of an element within the specified bounds.
 *
 * @param transform - Transform values of the currently dragged element
 * @param rect - The bounding rectangle of the element being transformed.
 * @param verticalBounds - The bounds within which the element should be restricted.
 * @returns The updated transform object with restricted y value.
 * @internal
 */
export function restrictToVerticalBounds(
  transform: DragAndDropTransform,
  rect: ClientRect,
  verticalBounds: {
    top: number;
    bottom: number;
  },
): DragAndDropTransform {
  const value = {
    ...transform,
  };

  if (
    rect.top >= verticalBounds.top &&
    rect.top + transform.y <= verticalBounds.top
  ) {
    value.y = verticalBounds.top - rect.top;
  } else if (
    rect.bottom <= verticalBounds.bottom &&
    rect.bottom + transform.y >= verticalBounds.bottom
  ) {
    value.y = verticalBounds.bottom - rect.bottom;
  }
  // Only with keyboard navigation:
  // When focusing the drag handle and scrolling the table so that the row is not visible anymore,
  // and then pressing SPACE/ENTER to start dragging, the dragged row's position needs to be adjusted
  // in a way where we need to make sure the row remains within the vertical bounds with the new transform value.
  else if (rect.top < verticalBounds.top) {
    const bottomTransformYLimit = verticalBounds.bottom - rect.bottom;
    const transformY =
      transform.y > 0
        ? verticalBounds.top - rect.top + transform.y
        : verticalBounds.top - rect.top;

    // adjust value.y to be the bottom bound if the new transform value would position the row outside the bottom vertical bound
    value.y =
      transformY > bottomTransformYLimit ? bottomTransformYLimit : transformY;
  } else if (rect.bottom > verticalBounds.bottom) {
    const topTransformYLimit = verticalBounds.top - rect.top;
    const transformY =
      transform.y < 0
        ? verticalBounds.bottom - rect.bottom + transform.y
        : verticalBounds.bottom - rect.bottom;

    // adjust value.y to be the top bound if the new transform value would position the row outside the top vertical bound
    value.y = transformY > topTransformYLimit ? transformY : topTransformYLimit;
  }

  return value;
}

/**
 * Creates a dnd-kit modifier to restrict the dragging to the table row area.
 * @param tableHeaderHeight - This height is subtracted from the parent element height.
 * @param containedVariant - Whether the table is using the contained visual variant.
 * @param virtualizationContainer - The table's virtualization container.
 * @returns dnd-kit modifier
 * @internal
 */
export function createRestrictToTableRowAreaModifier(
  tableHeaderHeight: number,
  containedVariant: boolean,
  virtualizationContainer?: HTMLDivElement,
): Modifier {
  return ({ transform, draggingNodeRect }) => {
    if (!draggingNodeRect || virtualizationContainer === undefined) {
      return transform;
    }

    // Restrict dragging to the vertical axis
    transform.x = 0;

    const parentRect = virtualizationContainer.getBoundingClientRect();
    // Restrict dragging to the table container minus borders
    const verticalBounds = {
      top: containedVariant
        ? parentRect?.top + tableHeaderHeight + getDataTableBorderWidth()
        : parentRect?.top + tableHeaderHeight,
      bottom: containedVariant
        ? parentRect?.bottom - getDataTableBorderWidth()
        : parentRect?.bottom,
    };

    return restrictToVerticalBounds(
      transform,
      draggingNodeRect,
      verticalBounds,
    );
  };
}
// #endregion Modifiers
